import React from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { Video, VideoFile, useVideoFilesQuery } from '@edx/graphql/generated';
import { translateVideoLevel } from '@edx/helpers/video-levels';
import { Link } from '@edx/components/link';
import { Avatar } from '@edx/components/avatar';
import { Badge } from '@edx/components/badge';
import { IconAngleLeft, IconDownloadCircle } from '@edx/components/icons';
import { Caption } from '@edx/components/typography';

type Props = {
  video: Video;
};

export const VideoDescription: React.FC<Props> = ({ video }) => {
  const router = useRouter();
  const signature = typeof router?.query?.signature === 'string' ? router?.query?.signature : undefined;

  const { data } = useVideoFilesQuery({ variables: { slug: video.slug, courseSlug: video.course.slug, signature } });

  const videoFiles = data?.video?.files;
  const files = React.useMemo(() => {
    if (!videoFiles) return video.files;

    return video.files.map((file, index) => {
      const url = videoFiles[index]?.id === file.id && videoFiles[index]?.url;
      return { ...file, url };
    });
  }, [videoFiles, video.files]);

  return (
    <section className="pt-6 pb-10">
      <Link className="group flex items-center space-x-1 font-body text-gray-500" href={video.course.resourcePath}>
        <IconAngleLeft className="w-4 h-4" />{' '}
        <span className="group-hover:underline">Volver a {video.course.name}</span>
      </Link>

      <div className="mt-2">
        <h1 className="text-3xl font-display text-gray-300 flex items-start">
          {video.nameUppercase} {video.isPublic && <Badge className="ml-2 text-xs font-medium">Gratis</Badge>}
        </h1>

        <h4 className="mt-4 text-xs font-body tracking-widest uppercase text-gray-300">
          {translateVideoLevel(video.level)}
        </h4>

        <p className="mt-6 mb-2 text-gray-500">{video.description}</p>

        {video.tags.length > 0 && (
          <div className="inline-flex items-center">
            <h4 className="mt-4 mr-3 text-xs font-body font-medium tracking-widest uppercase text-gray-300">Tags</h4>
            {video.tags.map((tag) => (
              <span key={`video-${video.id}-tag-${tag.id}`} className="mt-6 mr-2 mb-2 text-xs text-gray-500">
                {tag.name}
              </span>
            ))}
          </div>
        )}

        <div className="my-6 flex">
          <Link href={video.course.teacher.resourcePath} className="inline-flex items-center space-x-3">
            <Avatar name={video.course.teacher.name} src={video.course.teacher.avatarUrl} size={36} noBorder />
            <span className="uppercase text-gray-400 tracking-widest text-xs font-medium">
              {video.course.teacher.name}
            </span>
          </Link>
        </div>

        {video.files?.length ? (
          <div className="border-t border-gray-600 border-opacity-50">
            <Caption
              tag="h5"
              size="small"
              variant="semibold"
              id="lessonMaterialHeading"
              className="my-8 text-gray-500 tracking-widest"
            >
              Materiales de la lección
            </Caption>

            <ul className="space-y-1" aria-labelledby="lessonMaterialHeading">
              {(files as VideoFile[]).map((file: VideoFile) => (
                <li key={file.id}>
                  <a
                    href={file?.url || undefined}
                    title={file.name}
                    className={clsx('group text-sm text-gray-400 flex items-center', {
                      'cursor-not-allowed': !file?.url,
                    })}
                    download
                  >
                    <IconDownloadCircle className="w-6 h-6 mr-2" />
                    <span className="group-hover:underline">{file.name}</span>
                    <span className="ml-1 uppercase text-gray-500">{file.extension}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
};
